import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { DateNavigation } from 'components';
import { encryption } from 'utils';
import {
  useGetMenuStock,
  useGetMenuStockDetail,
  useGetMenuStockTemplate,
  useMeInfo,
  useMenuCategories,
  useUpdateMenuStock,
  useUpdateMenuStockTemplate
} from 'hooks';
import { IMenuCategories } from 'pages/MenuManage/MenuManageSchema';
import { MenuStockManageListWrapper, MenuStockManageBoardWrapper } from './menuStockManageStyle';
import MenuStockDetailModal from './menuStockDetailModal';
import { Container } from '../ReserveStatus/styles';
import MenuTemplateEditModal from './menuTemplateEditModal';
import { IMenuDetail, IMenuStockTemplate, IPageParams } from './types';

const DEFAULT_PAGE_PARAMS: IPageParams = {
  page: 1,
  size: 50
};

const TABLE_HEADERS = [
  { label: '메뉴명', width: '22%' },
  { label: '금액', width: '15%' },
  { label: '판매상태', width: '9%' },
  { label: '재고설정', width: '9%' },
  { label: '재고수량', width: '9%' },
  { label: '수량저장', width: '9%' },
  { label: '판매량', width: '9%' },
  { label: '남은 재고수량', width: '9%' },
  { label: '판매상세', width: '9%' }
] as const;

const MenuStockManage = () => {
  const [date, setDate] = useState<Date>(new Date());
  const [clickedMenuId, setClickedMenuId] = useState<string>('');
  const [menuDetailModalOpen, setMenuDetailModalOpen] = useState<boolean>(false);
  const [stockQuantities, setStockQuantities] = useState<Record<string, number | string>>({});
  const [stockManageStatus, setStockManageStatus] = useState<Record<string, boolean>>({});
  const [savingMenuIds, setSavingMenuIds] = useState<Set<string>>(new Set());

  const { data: originGetData } = useGetMenuStock(moment(date).format('YYYY-MM-DD'));
  const { menuStocks }: { menuStocks: IMenuDetail[] } = originGetData || {};
  const { mutateAsync: updateMenuStock } = useUpdateMenuStock();

  const { data: originMenuStockTemplateData }: { data: IMenuStockTemplate } = useGetMenuStockTemplate();

  const [pageParams, setPageParams] = useState<IPageParams>(DEFAULT_PAGE_PARAMS);
  const { data: menuStockDetail } = useGetMenuStockDetail(clickedMenuId, {
    serviceDate: moment(date).format('YYYY-MM-DD'),
    page: pageParams.page,
    size: pageParams.size
  });
  const { mutateAsync: updateMenuStockTemplate } = useUpdateMenuStockTemplate();

  const { selectedStore } = useMeInfo();
  const storeId = selectedStore?.sid;
  const { data: menuCategories }: { data: IMenuCategories[] } = useMenuCategories(storeId);

  const category = useMemo(() => {
    if (!menuCategories) {
      return [];
    }
    const categories = menuCategories.sort((a, b) => a.priority - b.priority).map((item) => item.categoryName);
    return Array.from(new Set(categories));
  }, [menuCategories]);

  const isDateInPast = useMemo(() => {
    const today = new Date();
    const selectedDate = new Date(date);

    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    return selectedDate < today;
  }, [date]);

  const onDateValueChange = (value: Date) => {
    setDate(value);
    setStockQuantities({});
    setStockManageStatus({});
  };

  const handleCheckMenu = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setStockManageStatus((prev) => ({
      ...prev,
      [id]: checked
    }));

    if (!checked) {
      setStockQuantities((prev) => ({
        ...prev,
        [id]: null
      }));
    } else {
      setStockQuantities((prev) => ({
        ...prev,
        [id]: 0
      }));
    }
  };

  const handleStockInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, dataset } = e.target;
    const itemId = dataset.id;
    const numericValue = value === '' ? '' : parseInt(value, 10) || 0;

    setStockQuantities((prev) => ({
      ...prev,
      [itemId]: numericValue
    }));
  };

  const handleSaveStockQuantity = async (e: React.MouseEvent<HTMLButtonElement>, rowData: IMenuDetail) => {
    const itemId = rowData.menuId;

    if (savingMenuIds.has(itemId)) return;

    const currentValue = stockQuantities[itemId];

    let numericValue;
    if (currentValue !== undefined) {
      if (typeof currentValue === 'string') {
        numericValue = parseInt(currentValue, 10) || 0;
      } else {
        numericValue = currentValue;
      }
    } else {
      numericValue = rowData.stock;
    }

    setStockQuantities((prev) => ({
      ...prev,
      [itemId]: numericValue
    }));

    const isStockSettingActive = stockManageStatus[itemId] ?? rowData.isActiveMenuStockSetting;
    const stockValue = isStockSettingActive ? numericValue : null;

    const params = {
      serviceDate: moment(date).format('YYYY-MM-DD'),
      menuId: itemId,
      isActiveMenuStockSetting: isStockSettingActive,
      stock: stockValue
    };

    setSavingMenuIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });

    try {
      await updateMenuStock(params);
    } finally {
      setSavingMenuIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const getInputValue = (menuId: string, originalStock: number | null | undefined) => {
    const userInput = stockQuantities[menuId];
    if (userInput !== undefined) return userInput;
    if (originalStock !== null && originalStock !== undefined) return originalStock;
    return '';
  };

  const handleMenuDetailModal = (menuId: string) => {
    setClickedMenuId(menuId);
    setMenuDetailModalOpen(true);
  };

  const handleMenuDetailModalClose = () => {
    setMenuDetailModalOpen(false);
    setClickedMenuId('');
    setPageParams(DEFAULT_PAGE_PARAMS);
  };

  const handlePageParamsChange = (newPageParams: IPageParams) => {
    setPageParams(newPageParams);
  };

  const [templateModalOpen, setTemplateModalOpen] = useState<boolean>(false);
  const handleTemplateModal = () => {
    setTemplateModalOpen(!templateModalOpen);
  };

  return (
    <Container id="menuStockManage">
      <DateNavigation date={date} onChange={onDateValueChange} infinite />

      <MenuStockManageListWrapper>
        <MenuStockManageBoardWrapper>
          <div className="menu-salesStats-info">
            <div>* 제휴점에서 판매 할 메뉴의 재고수량을 설정할 수 있습니다.</div>
            <div>
              * 원하는 메뉴에 &apos;재고설정&apos; 체크, &apos;재고수량&apos;을 입력 후 수량저장 버튼을 눌러주세요. (재고설정
              미체크 시, 수량 제한없이 판매됩니다.)
            </div>
            <div>
              * 판매상태가 &apos;일시품절&apos;이거나 &apos;판매종료&apos;인 경우, 재고수량이 있어도 사용자 주문이 불가능합니다.
            </div>
          </div>

          <div className="ele2">
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleTemplateModal}
                disabled={isDateInPast}
                style={{
                  outline: 'none',
                  border: 'none',
                  boxShadow: 'none',
                  cursor: isDateInPast ? 'not-allowed' : 'pointer',
                  opacity: isDateInPast ? 0.6 : 1
                }}
              >
                재고 관리 템플릿 수정
              </button>
            </div>
            <table className="menu-header-table">
              <thead>
                <tr className="menu-table-header" style={{ width: '100%' }}>
                  {TABLE_HEADERS.map(({ label, width }) => (
                    <React.Fragment key={label}>
                      <td className="menu-status-box" style={{ width }}>
                        <span className="font-size-18px font-color-white">{label}</span>
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
            </table>
          </div>

          <div className="scroll-box">
            <table
              className="menu-stockManage-table media-display-none"
              id="menu-stockManage-table"
              style={{ borderCollapse: 'collapse' }}
            >
              <tbody>
                {category &&
                  menuStocks &&
                  category.map((name) => {
                    const categoryData = menuStocks.filter((menu) => menu.category === name);
                    const { categoryId } = categoryData.length !== 0 ? categoryData[0] : { categoryId: '' };

                    return (
                      <React.Fragment key={(name as string) || 'etc'}>
                        <tr className={`category-row-${categoryId} menu-category`}>
                          <td className="media-display-none" colSpan={9}>
                            <div>
                              <span className="menu-group">{(name as string) || '기타'}</span>
                            </div>
                          </td>
                        </tr>

                        {categoryData &&
                          categoryData.map((data) => {
                            let status;
                            let fontColor = 'green';
                            if (data.status === 0) {
                              status = '판매종료';
                              fontColor = 'red';
                            } else if (data.status === 1) {
                              status = '판매중';
                              fontColor = 'green';
                            } else if (data.status === 2) {
                              status = '일시품절';
                              fontColor = 'orange';
                            } else if (data.status === 3) {
                              status = '재고부족';
                              fontColor = 'red';
                            }

                            return (
                              <tr key={data.menuId} className={data.status === 2 ? 'inactive_row' : undefined}>
                                <td className="media-display-none bgc-white menu-table-content" style={{ width: '22%' }}>
                                  {data.menuName}
                                </td>
                                <td className="media-display-none bgc-white menu-table-content" style={{ width: '15%' }}>
                                  {data.price ? `${encryption(data.price)}원` : '-'}
                                </td>
                                <td
                                  className="media-display-none bgc-white menu-table-content"
                                  style={{ width: '9%', color: fontColor }}
                                >
                                  {status}
                                </td>
                                <td className="media-display-none bgc-white menu-table-content" style={{ width: '9%' }}>
                                  <input
                                    type="checkbox"
                                    onChange={(e) => handleCheckMenu(e)}
                                    className="custom-checkbox"
                                    name={data.menuId}
                                    id={data.menuId}
                                    checked={stockManageStatus[data.menuId] ?? data.isActiveMenuStockSetting}
                                    disabled={isDateInPast}
                                  />
                                </td>
                                <td className="media-display-none bgc-white menu-table-content" style={{ width: '9%' }}>
                                  <input
                                    type="number"
                                    min="0"
                                    value={getInputValue(data.menuId, data.stock)}
                                    onChange={(e) => handleStockInputChange(e)}
                                    data-id={data.menuId}
                                    className="form-control input-number"
                                    style={{ textAlign: 'center', fontSize: '16px' }}
                                    disabled={isDateInPast || !(stockManageStatus[data.menuId] ?? data.isActiveMenuStockSetting)}
                                  />
                                </td>
                                <td className="media-display-none bgc-white menu-table-content" style={{ width: '9%' }}>
                                  <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={(e) => handleSaveStockQuantity(e, data)}
                                    disabled={isDateInPast || savingMenuIds.has(data.menuId)}
                                    style={{
                                      outline: 'none',
                                      border: 'none',
                                      boxShadow: 'none',
                                      cursor: isDateInPast || savingMenuIds.has(data.menuId) ? 'not-allowed' : 'pointer',
                                      opacity: isDateInPast || savingMenuIds.has(data.menuId) ? 0.6 : 1
                                    }}
                                  >
                                    수량저장
                                  </button>
                                </td>
                                <td className="media-display-none bgc-white menu-table-content" style={{ width: '9%' }}>
                                  {data.sales || data.sales === 0 ? encryption(data.sales) : '-'}
                                </td>
                                <td
                                  className="media-display-none bgc-white menu-table-content"
                                  style={{
                                    width: '9%',
                                    color: data.remainingStock < 0 ? 'red' : 'inherit'
                                  }}
                                >
                                  {data.remainingStock || data.remainingStock === 0 ? encryption(data.remainingStock) : '-'}
                                </td>
                                <td className="media-display-none bgc-white menu-table-content" style={{ width: '9%' }}>
                                  <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={() => handleMenuDetailModal(data.menuId)}
                                    style={{
                                      outline: 'none',
                                      border: 'none',
                                      boxShadow: 'none'
                                    }}
                                  >
                                    판매상세
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </MenuStockManageBoardWrapper>
      </MenuStockManageListWrapper>

      <MenuStockDetailModal
        open={menuDetailModalOpen}
        onClose={handleMenuDetailModalClose}
        menuDetailModalData={menuStockDetail || {}}
        pageParams={pageParams}
        onPageParamsChange={handlePageParamsChange}
      />

      {originMenuStockTemplateData && (
        <MenuTemplateEditModal
          open={templateModalOpen}
          onClose={handleTemplateModal}
          menuDetailModalData={originMenuStockTemplateData}
          category={category}
          onUpdateMenuStockTemplate={updateMenuStockTemplate}
        />
      )}
    </Container>
  );
};

export default MenuStockManage;
